import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { concat, from, Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import {
  CitationEntry,
  ValidateApiResponse,
  ValidationResults,
} from '../model/validation-service.model';
import {
  SnackbarErrorAction,
  SnackbarInfoAction,
} from '../store/actions/app.custom-actions';
import { AppStore } from '../store';
import { CommonConstants } from '../shared/common.constant';
import { getFeatureFlags } from '../store/selectors/app.custom-selectors';
import { FEATURE_FLAGS } from '../constants/launch-darkly-feature-flags.constants';
import { setDocumentValidationStatus } from '../modules/wk-document-decorator/state/decorators.actions';
import { getDocumentValidationStatus } from '../modules/wk-document-decorator/state/selectors/get.documentvalidationstatus';

const {
  VALIDATION_STATUS: { IN_PROGRESS, INVALID, PROCESSING_FAILED, VALID },
} = CommonConstants;
const VALIDATION_STATUS_KEY = 'apollo:validationStatus';
const VALIDATION_RESULTS_KEY = 'apollo:validationResults';
const VALIDATION_POLL_INTERVAL_MS = 10000;
const VALIDATION_POLL_MAX_ATTEMPTS = 6;

@Injectable({
  providedIn: 'root',
})
export class WkValidationService {
  private readonly apiService: WkAlfrescoApiService;
  private readonly store: Store<AppStore>;
  private readonly activeOrchestrationSyncByNode = new Map<
    string,
    Observable<string>
  >();
  constructor(private readonly injector: Injector) {
    this.apiService =
      this.injector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
    this.store = this.injector.get(Store);
  }

  /**
   * Call validation api
   * @private
   * @param {string[]}
   * @returns {Observable<ValidateApiResponse>}
   */
  private callValidationAPI(
    objectIds: string[]
  ): Observable<ValidateApiResponse> {
    const siteId = (localStorage.getItem('currentSite') ?? '').toLowerCase();
    const apiUrl = `alfresco/s/wk/core/validation/${siteId}/validate`;
    return from(
      this.apiService.callContentClientCustomApi({
        httpMethod: 'POST',
        path: apiUrl,
        bodyParam: { objectIds },
        contentTypes: ['application/json'],
        accepts: ['application/json'],
      })
    );
  }

  /**
   * Reads orchestration metadata and extracts final status plus validation results for a node.
   */
  private getOrchestrationValidationData(
    nodeId: string
  ): Observable<{ status: string; validationResults: ValidationResults[] }> {
    return from(
      this.apiService.callContentClientCustomApi({
        path: `alfresco/s/wk/nodes/${nodeId}/orchestrated-properties?fields=all`,
        httpMethod: 'GET',
        contentTypes: ['application/json'],
        accepts: ['application/json'],
      })
    ).pipe(
      map((response: any) => {
        const currentValidationResults =
          response?.documentValidationStatus?.currentValidationResults;
        const rawStatus =
          response?.documentValidationStatus?.currentValidationStatus ||
          currentValidationResults?.html5?.[VALIDATION_STATUS_KEY];
        return {
          status: rawStatus,
          validationResults: this.buildValidationResults(
            currentValidationResults
          ),
        };
      })
    );
  }

  private flattenCitationResults(
    citations: CitationEntry[] = []
  ): ValidationResults[] {
    return (citations || []).reduce<ValidationResults[]>(
      (acc, entry) => acc.concat(entry?.[VALIDATION_RESULTS_KEY] ?? []),
      []
    );
  }

  private buildValidationResults(
    currentValidationResults: any
  ): ValidationResults[] {
    const validationSources: Array<{
      type: string;
      results: ValidationResults[];
    }> = [
      {
        type: 'HTML',
        results:
          currentValidationResults?.html5?.[VALIDATION_RESULTS_KEY] ?? [],
      },
      {
        type: 'NUMBERED CITATIONS',
        results: this.flattenCitationResults(
          currentValidationResults?.citations?.numbered
        ),
      },
      {
        type: 'UNNUMBERED CITATIONS',
        results: this.flattenCitationResults(
          currentValidationResults?.citations?.unnumbered
        ),
      },
    ];

    return validationSources.reduce<ValidationResults[]>(
      (acc, { type, results }) =>
        acc.concat(results.map((entry) => ({ ...entry, type }))),
      []
    );
  }

  /**
   * Returns true only when validation has reached a terminal state.
   */
  private isFinalValidationStatus(status: string): boolean {
    return (
      status === VALID || status === INVALID || status === PROCESSING_FAILED
    );
  }

  /**
   * Updates status and optionally replaces validation results.
   * When results are not provided, existing results are preserved.
   */
  private updateValidationStatusInStore(
    status: string,
    validationResults?: ValidationResults[]
  ): void {
    this.store
      .select(getDocumentValidationStatus)
      .pipe(take(1))
      .subscribe((currentValidationStatus) => {
        this.store.dispatch(
          setDocumentValidationStatus({
            documentValidationStatus: {
              status,
              validationResults:
                validationResults ??
                (currentValidationStatus?.validationResults || []),
            },
          })
        );
      });
  }

  /**
   * Resolves LaunchDarkly toggle state for document validation features.
   */
  private isValidationFeatureEnabled(): Observable<boolean> {
    return this.store.select(getFeatureFlags).pipe(
      take(1),
      map((flags) => !!flags?.[FEATURE_FLAGS.ENABLE_APOLLO_DOCUMENT_VALIDATION])
    );
  }

  /**
   * Emits a final toast message once validation resolves to a terminal status.
   */
  private notifyFinalValidationStatus(
    status: string,
    isValidationEnabled: boolean
  ): void {
    if (!isValidationEnabled) {
      return;
    }

    const statusNotifications: Record<
      string,
      () => SnackbarInfoAction | SnackbarErrorAction
    > = {
      [VALID]: () =>
        new SnackbarInfoAction(
          'APP.MESSAGES.INFO.VALIDATION_STATUS_VALID_ON_PREVIEW'
        ),
      [INVALID]: () =>
        new SnackbarErrorAction(
          'APP.MESSAGES.ERRORS.VALIDATION_STATUS_INVALID_ON_PREVIEW'
        ),
      [PROCESSING_FAILED]: () =>
        new SnackbarErrorAction(
          'APP.MESSAGES.ERRORS.VALIDATION_STATUS_PROCESSING_FAILED_ON_PREVIEW'
        ),
    };
    const notification = statusNotifications[status];

    if (notification) {
      this.store.dispatch(notification());
    }
  }

  /**
   * Polls orchestration status until a terminal status is found or attempts are exhausted.
   */
  private pollForFinalValidationStatus(
    nodeId: string
  ): Observable<{ status: string; validationResults: ValidationResults[] }> {
    return timer(0, VALIDATION_POLL_INTERVAL_MS).pipe(
      take(VALIDATION_POLL_MAX_ATTEMPTS),
      // Avoid overlapping requests: do not start the next poll tick while the current request is in flight.
      exhaustMap(() =>
        this.getOrchestrationValidationData(nodeId).pipe(
          catchError(() => of(null))
        )
      ),
      filter(
        (
          result
        ): result is {
          status: string;
          validationResults: ValidationResults[];
        } => !!result && this.isFinalValidationStatus(result.status)
      ),
      take(1)
    );
  }

  /**
   * Synchronizes store status from orchestration and deduplicates concurrent requests per node.
   * Accepts an optional resolved feature-flag value to avoid extra selector reads.
   */
  public syncValidationStatusFromOrchestration(
    nodeId: string,
    isValidationEnabled: boolean | undefined = undefined
  ): Observable<string> {
    const existingSync$ = this.activeOrchestrationSyncByNode.get(nodeId);
    if (existingSync$) {
      return existingSync$;
    }

    const validationEnabled$ =
      typeof isValidationEnabled === 'boolean'
        ? of(isValidationEnabled)
        : this.isValidationFeatureEnabled();

    const sync$ = this.pollForFinalValidationStatus(nodeId).pipe(
      withLatestFrom(validationEnabled$),
      map(([result, validationEnabled]) => {
        this.updateValidationStatusInStore(
          result.status,
          result.validationResults
        );
        this.notifyFinalValidationStatus(result.status, validationEnabled);
        return result.status;
      }),
      finalize(() => {
        this.activeOrchestrationSyncByNode.delete(nodeId);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.activeOrchestrationSyncByNode.set(nodeId, sync$);
    return sync$;
  }

  /**
   * Starts validation, sets immediate IN_PROGRESS status, and emits final status for single-node requests.
   */
  public getValidationStatus(nodeIds: string[]): Observable<string> {
    return this.callValidationAPI(nodeIds).pipe(
      take(1),
      withLatestFrom(this.isValidationFeatureEnabled()),
      switchMap(([_response, isValidationEnabled]) => {
        // Per updated contract, any HTTP 200 response is success.
        // jobId > 0: async CSB job for multiple documents.
        // jobId === 0: synchronous single-document validation completed.
        // HTTP 500 failures are handled exclusively by catchError below.
        if (isValidationEnabled) {
          this.store.dispatch(
            new SnackbarInfoAction('APP.MESSAGES.INFO.VALIDATION_SUCCESS')
          );
        }

        this.updateValidationStatusInStore(IN_PROGRESS, []);
        const inProgress$ = of(IN_PROGRESS);
        if (nodeIds?.length !== 1 || !nodeIds[0]) {
          return inProgress$;
        }

        return concat(
          inProgress$,
          this.syncValidationStatusFromOrchestration(
            nodeIds[0],
            isValidationEnabled
          )
        );
      }),
      catchError((err) => {
        // HTTP 500 responses indicate validation failure per updated contract.
        return this.isValidationFeatureEnabled().pipe(
          tap((isValidationEnabled) => {
            if (isValidationEnabled) {
              this.store.dispatch(
                new SnackbarErrorAction('APP.MESSAGES.INFO.VALIDATION_FAILURE')
              );
            }
          }),
          switchMap(() => throwError(() => err))
        );
      })
    );
  }
}
